import { gql } from '@apollo/client';

/**
 * User Groups (Group Security v3, collector model) — GraphQL operations for the open-api
 * `user-group.resolver` (research/04-feedback §7.2). Distinct from `security-groups.ts` (the v1
 * scope-group ops, superseded). "Workspace" == Team → the team-scope field is `teamId`; the UI
 * renders it as "Workspace". Enums are UPPERCASE on the wire (DEVICE/ASSET/…). Group visibility
 * (PUBLIC/PRIVATE) is deprecated — every group is account-wide, so that field is no longer selected.
 *
 * Response typing is done with hand-written interfaces at the call sites (no codegen in this repo).
 */

const USER_GROUP_LIST_FIELDS = gql`
  fragment UserGroupListFields on UserGroupListItem {
    _id
    accountId
    name
    description
    color
    isSystem
    teamId
    teamName
    roleIds
    memberCount
    resourceCount
    workspaceCount
    resourceCountByType {
      resourceType
      count
    }
    createdAt
    lastUpdatedDate
  }
`;

export const USER_GROUPS_QUERY = gql`
  query UserGroups($query: QueryUserGroupInput) {
    userGroups(query: $query) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const USER_GROUP_DETAIL_QUERY = gql`
  query UserGroupDetail($id: String!) {
    userGroupDetail(id: $id) {
      _id
      accountId
      name
      description
      color
      teamId
      teamName
      roleIds
      createdBy
      createdAt
      lastUpdatedDate
      memberList {
        _id
        name
        email
        username
        hasMfa
      }
      invites {
        email
        name
        invitedAt
      }
      resourceList {
        resourceType
        resourceId
        resourceName
        resourcePath
        teamId
        teamName
        addedAt
      }
      workspaces {
        teamId
        teamName
        role
        homeFolders {
          type
          groupId
          path
          name
        }
        canEditHomePath
      }
    }
  }
`;

export const WORKSPACE_MEMBERS_QUERY = gql`
  query WorkspaceMembers($workspaceId: String!) {
    workspaceMembers(workspaceId: $workspaceId) {
      _id
      name
      email
      roles
      sources {
        type
        groupId
        groupName
        role
      }
    }
  }
`;

export const WORKSPACE_GROUPS_QUERY = gql`
  query WorkspaceGroups($workspaceId: String!) {
    workspaceGroups(workspaceId: $workspaceId) {
      groupId
      groupName
      role
      memberCount
      addedAt
      homeFolders {
        type
        groupId
        path
        name
      }
      canEditHomePath
    }
  }
`;

// Home-folder picker (§12 per-type): folders of ONE content type in a workspace, via the STANDARD
// `groups` resolver (not a special candidates API). Setting a group's home is admin-only, and admins
// bypass the folder read filter, so this lists ALL folders of that type — paginated, no 100-cap. Only
// the fields the dropdown needs are selected.
export const FOLDER_PICKER_GROUPS_QUERY = gql`
  query FolderPickerGroups($query: QueryGroupInput, $teamId: String, $first: Int) {
    groups(query: $query, teamId: $teamId, first: $first) {
      page {
        edges {
          node {
            _id
            name
            path
            teamId
          }
        }
      }
    }
  }
`;

export const USER_GROUP_RESOURCE_CANDIDATES_QUERY = gql`
  query UserGroupResourceCandidates(
    $teamId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $folderType: UserGroupResourceType
  ) {
    userGroupResourceCandidates(
      teamId: $teamId
      resourceType: $resourceType
      search: $search
      folderType: $folderType
    ) {
      resourceId
      name
      path
      teamId
      securityGroupIds
    }
  }
`;

// Model B: objects of a type a group actually grants (securityGroupIds ∋ group), paged + name search.
// Powers the reworked Resources tab. `securityGroupIds` is returned so the FE can remove a grant by
// pulling the group id out of the set and calling SET_RESOURCE_SECURITY_GROUPS.
export const GROUP_GRANTED_RESOURCES_QUERY = gql`
  query GroupGrantedResources(
    $groupId: String!
    $resourceType: UserGroupResourceType!
    $search: String
    $first: Int
    $offset: Int
    $folderType: UserGroupResourceType
  ) {
    groupGrantedResources(
      groupId: $groupId
      resourceType: $resourceType
      search: $search
      first: $first
      offset: $offset
      folderType: $folderType
    ) {
      total
      items {
        resourceType
        resourceId
        name
        path
        teamId
        teamName
        securityGroupIds
        folderType
      }
    }
  }
`;

export const GROUP_ROLE_USAGE_QUERY = gql`
  query GroupRoleUsage($roleIds: [String!]!) {
    groupRoleUsage(roleIds: $roleIds) {
      roleId
      count
    }
  }
`;

export const RESOURCE_GRANTING_GROUPS_QUERY = gql`
  query ResourceGrantingGroups($resourceType: UserGroupResourceType!, $resourceId: String!) {
    resourceGrantingGroups(resourceType: $resourceType, resourceId: $resourceId) {
      _id
      name
      color
      roleIds
      memberCount
    }
  }
`;

// --- Mutations ---

export const CREATE_USER_GROUP = gql`
  mutation CreateUserGroup($input: CreateUserGroupInput!) {
    createUserGroup(input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const UPDATE_USER_GROUP = gql`
  mutation UpdateUserGroup($id: String!, $input: UpdateUserGroupInput!) {
    updateUserGroup(id: $id, input: $input) {
      ...UserGroupListFields
    }
  }
  ${USER_GROUP_LIST_FIELDS}
`;

export const DELETE_USER_GROUP = gql`
  mutation DeleteUserGroup($id: String!) {
    deleteUserGroup(id: $id) {
      success
      affectedMembers
      removedResourceGrants
      removedWorkspaceEntries
    }
  }
`;

export const ADD_USER_GROUP_MEMBERS = gql`
  mutation AddUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    addUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_MEMBERS = gql`
  mutation RemoveUserGroupMembers($groupId: String!, $userIds: [String!]!) {
    removeUserGroupMembers(groupId: $groupId, userIds: $userIds) {
      count
    }
  }
`;

// §12 group-only — revoke a PENDING email invite off the group (userGroups.invites[]).
export const REVOKE_GROUP_INVITE = gql`
  mutation RevokeGroupInvite($groupId: String!, $email: String!) {
    revokeGroupInvite(groupId: $groupId, email: $email) {
      success
    }
  }
`;

export const ADD_USER_GROUP_RESOURCES = gql`
  mutation AddUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    addUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

export const REMOVE_USER_GROUP_RESOURCES = gql`
  mutation RemoveUserGroupResources($groupId: String!, $resources: [ResourceRefInput!]!) {
    removeUserGroupResources(groupId: $groupId, resources: $resources) {
      count
    }
  }
`;

/**
 * Set which security groups a folder/item is shared to (Group Security Model B). Grants live on the
 * object's `securityGroupIds`. `groupIds`: null ⇒ inherit the folder's grant (unset), [] ⇒ shared to
 * nobody (per-item unshare), [ids] ⇒ explicit share. resourceType 'GROUP' = folder.
 */
export const SET_RESOURCE_SECURITY_GROUPS = gql`
  mutation SetResourceSecurityGroups(
    $resourceType: UserGroupResourceType!
    $resourceId: String!
    $groupIds: [String!]
  ) {
    setResourceSecurityGroups(
      resourceType: $resourceType
      resourceId: $resourceId
      groupIds: $groupIds
    ) {
      success
    }
  }
`;

export const ADD_GROUP_TO_WORKSPACE = gql`
  mutation AddGroupToWorkspace(
    $workspaceId: String!
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
    $autoCreateHomeFolder: Boolean
  ) {
    addGroupToWorkspace(
      workspaceId: $workspaceId
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
      autoCreateHomeFolder: $autoCreateHomeFolder
    ) {
      success
    }
  }
`;

export const UPDATE_GROUP_WORKSPACE_ROLE = gql`
  mutation UpdateGroupWorkspaceRole($workspaceId: String!, $groupId: String!, $role: String!) {
    updateGroupWorkspaceRole(workspaceId: $workspaceId, groupId: $groupId, role: $role) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_WORKSPACE = gql`
  mutation RemoveGroupFromWorkspace($workspaceId: String!, $groupId: String!) {
    removeGroupFromWorkspace(workspaceId: $workspaceId, groupId: $groupId) {
      success
    }
  }
`;

export const ADD_GROUP_TO_ACCOUNT = gql`
  mutation AddGroupToAccount(
    $groupId: String!
    $role: String!
    $homeFolders: [GroupHomeFolderInput!]
    $canEditHomePath: Boolean
    $autoCreateHomeFolder: Boolean
  ) {
    addGroupToAccount(
      groupId: $groupId
      role: $role
      homeFolders: $homeFolders
      canEditHomePath: $canEditHomePath
      autoCreateHomeFolder: $autoCreateHomeFolder
    ) {
      success
    }
  }
`;

export const REMOVE_GROUP_FROM_ACCOUNT = gql`
  mutation RemoveGroupFromAccount($groupId: String!) {
    removeGroupFromAccount(groupId: $groupId) {
      success
    }
  }
`;
